import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, map, firstValueFrom } from 'rxjs';
import { AppStateService } from '../app-state/app-state.service';
import { API_BASE, PROJECT } from '../../constants/general.constants';
import {
  Connection,
  ConnectionTestResult,
  CreateConnectionRequest,
  UpdateConnectionRequest,
  ConnectionType,
  LLMType,
  BoxAppOption
} from '@app/shared/interfaces';
import type { ObjectStorageConnection } from '@app/shared/interfaces/object-storage.interface';
import { ObjectStorageProvider, ObjectStorageAuthType } from '@app/shared/interfaces/object-storage.interface';

@Injectable({
  providedIn: 'root'
})
export class ConnectionService {
  readonly #httpClient = inject(HttpClient);
  readonly #appState = inject(AppStateService);

  baseUrl = computed(() => this.buildConnectionUrl(this.#appState.projectId()));

  readonly #connections = signal<Connection[]>([]);
  connections = this.#connections.asReadonly();

  constructor() {
    // Don't auto-initialize - let components trigger loading when they have valid project IDs
  }

  /**
   * Build connection URL for a given project
   */
  private buildConnectionUrl(projectId: number, connectionId?: number): string {
    const baseUrl = `${API_BASE}/${PROJECT}/${projectId}/connection`;
    return connectionId ? `${baseUrl}/${connectionId}` : baseUrl;
  }

  /**
   * Execute HTTP request with standard error handling and signal updates
   * @param projectId The project ID
   * @param request The HTTP request observable
   * @param updateFn Function to update the signal with new data (if needed)
   * @returns Observable of the request result
   */
  private executeRequest<T>(
    projectId: number,
    request: Observable<T>,
    updateFn?: (result: T, connections: Connection[]) => Connection[]
  ): Observable<T> {
    return request.pipe(
      tap((result) => {
        // Only update the signal when:
        // - No project is currently "open" in appState (e.g. dialog opened from project list) → always allow
        // - The request belongs to the currently open project → normal in-project path
        // This prevents a response for project B from overwriting the signal while the user is in project A.
        const activeProjectId = this.#appState.projectId();
        if (updateFn && (!activeProjectId || activeProjectId === projectId)) {
          this.#connections.set(updateFn(result, this.#connections()));
        }
      })
    );
  }

  /**
   * Get all connections for the current project
   */
  async refreshConnections(): Promise<Connection[]> {
    const currentProjectId = this.#appState.projectId();

    if (!currentProjectId || currentProjectId <= 0) {
      console.warn('No valid project ID available for refreshConnections');
      return [];
    }

    return firstValueFrom(this.getConnections(currentProjectId));
  }

  /**
   * Get all connections for a specific project
   */
  getConnections(projectId: number): Observable<Connection[]> {
    if (!projectId || projectId <= 0) {
      console.error('Invalid project ID provided to getConnections:', projectId);
      return new Observable(subscriber => {
        subscriber.error(new Error(`Invalid project ID: ${projectId}`));
      });
    }

    const url = this.buildConnectionUrl(projectId);
    return this.executeRequest<Connection[]>(
      projectId,
      this.#httpClient.get<Connection[]>(url),
      (connections) => connections // Direct replacement of the connections array
    );
  }

  /**
   * Get a specific connection by ID
   */
  getConnection(projectId: number, connectionId: number): Observable<Connection> {
    if (!projectId || projectId <= 0) {
      console.error('Invalid project ID provided to getConnection:', projectId);
      return new Observable(subscriber => {
        subscriber.error(new Error(`Invalid project ID: ${projectId}`));
      });
    }

    const url = this.buildConnectionUrl(projectId, connectionId);
    return this.executeRequest<Connection>(
      projectId,
      this.#httpClient.get<Connection>(url)
    );
  }

  /**
   * Create a new connection
   */
  createConnection(projectId: number, request: CreateConnectionRequest): Observable<Connection> {
    if (!projectId || projectId <= 0) {
      console.error('Invalid project ID provided to createConnection:', projectId);
      return new Observable(subscriber => {
        subscriber.error(new Error(`Invalid project ID: ${projectId}`));
      });
    }

    const url = this.buildConnectionUrl(projectId);
    return this.executeRequest<Connection>(
      projectId,
      this.#httpClient.post<Connection>(url, request),
      (connection, connections) => [...connections, connection]
    );
  }

  /**
   * Update an existing connection
   */
  updateConnection(projectId: number, connectionId: number, request: UpdateConnectionRequest): Observable<Connection> {
    if (!projectId || projectId <= 0) {
      console.error('Invalid project ID provided to updateConnection:', projectId);
      return new Observable(subscriber => {
        subscriber.error(new Error(`Invalid project ID: ${projectId}`));
      });
    }

    const url = this.buildConnectionUrl(projectId, connectionId);
    return this.executeRequest<Connection>(
      projectId,
      this.#httpClient.put<Connection>(url, request),
      (updatedConnection, connections) => {
        const idx = connections.findIndex((c) => c.connectionId === connectionId);
        if (idx >= 0) {
          const updated = [...connections];
          updated[idx] = updatedConnection;
          return updated;
        }
        return connections;
      }
    );
  }

  /**
   * Test a connection (validates credentials and connectivity)
   * @returns Observable with test result (success, message, optional details like filesCount)
   */
  testConnection(
    projectId: number,
    connectionId: number
  ): Observable<ConnectionTestResult> {
    if (!projectId || projectId <= 0 || !connectionId || connectionId <= 0) {
      return new Observable(subscriber => {
        subscriber.error(new Error('Invalid project or connection ID for test'));
      });
    }

    const url = `${this.buildConnectionUrl(projectId, connectionId)}/test`;
    return this.#httpClient.post<ConnectionTestResult>(url, {});
  }

  /**
   * Delete a connection
   */
  deleteConnection(projectId: number, connectionId: number): Observable<void> {
    if (!projectId || projectId <= 0) {
      console.error('Invalid project ID provided to deleteConnection:', projectId);
      return new Observable(subscriber => {
        subscriber.error(new Error(`Invalid project ID: ${projectId}`));
      });
    }

    const url = this.buildConnectionUrl(projectId, connectionId);
    return this.executeRequest<void>(
      projectId,
      this.#httpClient.delete<void>(url),
      (_, connections) => connections.filter((c) => c.connectionId !== connectionId)
    );
  }

  /**
   * List enabled global services (for the connection dialog dropdown).
   * Returns service ID, name, and description.
   */
  getGlobalServices(): Observable<{ serviceId: number; name: string; description: string }[]> {
    const url = `${API_BASE}/admin/service`;
    return this.#httpClient.get<any[]>(url).pipe(
      map(services => services
        .filter(s => s.enabled)
        .map(s => ({ serviceId: s.serviceId, name: s.name, description: s.description }))
      )
    );
  }

  /**
   * List the platform-managed Box apps available for a registry-backed Box
   * connection. Gated server-side by the connection-create permission; returns
   * id/name/description only (never secrets). SCRUM-614.
   */
  getBoxApps(projectId: number): Observable<BoxAppOption[]> {
    const url = `${API_BASE}/${PROJECT}/${projectId}/box-apps`;
    return this.#httpClient.get<BoxAppOption[]>(url);
  }

  /**
   * Get connections filtered by type
   */
  getConnectionsByType(projectId: number, type: ConnectionType): Observable<Connection[]> {
    return this.getConnections(projectId).pipe(
      map(connections => connections.filter(c => c.type === type))
    );
  }

  /**
   * Get the current project ID from app state
   */
  getCurrentProjectId(): number {
    return this.#appState.projectId();
  }

  /**
   * Wait for a valid project ID to be available
   * @param timeoutMs Maximum time to wait in milliseconds (default: 5000ms)
   * @returns Promise that resolves with the project ID or rejects if timeout
   */
  async waitForValidProjectId(timeoutMs: number = 5000): Promise<number> {
    const startTime = Date.now();

    while (Date.now() - startTime < timeoutMs) {
      const projectId = this.#appState.projectId();
      if (projectId && projectId > 0) {
        return projectId;
      }

      // Wait 100ms before checking again
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    throw new Error(`Timeout waiting for valid project ID after ${timeoutMs}ms`);
  }

  /**
   * Check if we have a valid project ID available
   */
  hasValidProjectId(): boolean {
    const projectId = this.#appState.projectId();
    return !!(projectId && projectId > 0);
  }

  /**
   * Get mock connections for development/demo when backend is unavailable.
   * Use with useMockData flag in ConnectionsComponent.
   */
  static getMockConnections(): Connection[] {
    return [
      {
        connectionId: 1,
        projectId: 1,
        name: 'Production PostgreSQL',
        description: 'Main production database',
        type: ConnectionType.Database,
        config: {
          host: 'db.example.com',
          port: '5432',
          dbname: 'production',
          username: 'admin'
        }
      },
      {
        connectionId: 2,
        projectId: 1,
        name: 'OpenAI API',
        description: 'GPT-4 and embeddings',
        type: ConnectionType.LLM,
        config: {
          llmType: LLMType.OpenAI,
          apiKeySecretId: 1,
          endpoint: 'https://api.openai.com/v1'
        }
      },
      {
        connectionId: 3,
        projectId: 1,
        name: 'Newton DataFusion',
        description: 'Query engine cluster',
        type: ConnectionType.DataFusion,
        config: {
          url: 'http://newton-rs:8080'
        }
      },
      {
        connectionId: 4,
        projectId: 1,
        name: 'Box Enterprise',
        description: 'Document storage',
        type: ConnectionType.Box,
        config: {
          authType: 'jwt',
          apiKeySecretId: 2
        }
      }
    ];
  }

  /**
   * Get mock object storage connections for development/demo.
   */
  static getMockObjectStorageConnections(): ObjectStorageConnection[] {
    return [
      {
        connectionId: 101,
        projectId: 1,
        name: 'S3 Data Lake',
        description: 'AWS S3 bucket for analytics',
        provider: ObjectStorageProvider.S3,
        authType: ObjectStorageAuthType.AccessKey,
        bucket: 'analytics-data',
        region: 'us-east-1',
        endpoint: 's3.amazonaws.com'
      },
      {
        connectionId: 102,
        projectId: 1,
        name: 'GCS Backups',
        provider: ObjectStorageProvider.GCS,
        authType: ObjectStorageAuthType.ServiceAccount,
        bucket: 'project-backups',
        region: 'us-central1'
      }
    ];
  }

}
